/* oxlint-disable typescript/consistent-return -- LINTER migration follow up */
/* oxlint-disable grafit-oxlint/no-tailwind-font-classes -- Typography 2 migration */
// oxlint-disable max-lines-per-function -- LINTER migration follow up
import { Button } from "@grafit/design-system/components/molecules/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@grafit/design-system/components/molecules/card";
import { toast } from "@grafit/design-system/components/molecules/toast";
import { useLocalStorage } from "@grafit/design-system/hooks/use-local-storage";
import { cn } from "@grafit/design-system/utils/cn";
import { Trans, useTranslation } from "@grafit/i18n";
import { TenantConfigResponseSchema } from "@grafit/shared/domain/auth/schema";
import { alertDevs, log } from "@grafit/shared/observability/sentry-front";
import { isPreview } from "@grafit/shared/utils/envs.front";
import * as React from "react";
import { useCallback, useState } from "react";
import { z } from "zod";
import { isSigningOut } from "./auth-client";
import { useHandleOnboarding } from "./hooks/use-handle-onboarding";
import { OtherMethods } from "./sign-in/other-methods";
import { SignInForm } from "./sign-in/sign-in-form";
import { signInWithSSO } from "./sso-sign-in";
import { assertOnline } from "./utils/assert-online";
import { fetchWithRetry } from "./utils/fetch-with-retry";
import { getBaseUrl } from "./utils/get-base-url";
import { reportTenantConfigFailure } from "./utils/report-tenant-config-failure";
import { AUTH_METHOD_SSO_PROVIDER_ID_KEY, hasActiveSSOAttempt } from "./utils/sso-state";

export const AUTH_METHOD_PREFERRED_EMAIL_KEY = "auth-method-preferred-email";

export const SignInPage: React.MemoExoticComponent<React.FC<{ ssoError?: boolean | undefined }>> = React.memo(
  ({ ssoError = false }: { ssoError?: boolean }) => {
    const { t } = useTranslation();
    const searchParams = new URLSearchParams(window.location.search);
    const isSuccessPage = searchParams.has("success") && searchParams.get("success") === "true";
    const hasExpiredError = searchParams.has("error") && searchParams.get("error") === "EXPIRED_TOKEN";
    const hasInvalidError = searchParams.has("error") && searchParams.get("error") === "INVALID_TOKEN";

    const [validatedEmail, setValidatedEmail] = useLocalStorage(AUTH_METHOD_PREFERRED_EMAIL_KEY, "", z.string());
    /*
    Values:
    - string: SSO provider ID
    - false: SSO is not available
    - null: SSO provider ID is not set or could not be loaded
  */
    const [ssoProviderId, setSSOProviderId] = useLocalStorage(
      AUTH_METHOD_SSO_PROVIDER_ID_KEY,
      null,
      z.union([z.string(), z.literal(false), z.null()]),
    );
    const [isLoadingSSOProviderId, setIsLoadingSSOProviderId] = useState<boolean>(false);
    const [isLoadingSSOSignIn, setIsLoadingSSOSignIn] = useState<boolean>(false);

    const resetValidatedEmail = useCallback(() => {
      setValidatedEmail("");
      setSSOProviderId(null);
    }, [setValidatedEmail, setSSOProviderId]);

    const ssoSignIn = useCallback(
      (
        providerId: string | false | null,
        { ssoRedirectPath, userEmail = validatedEmail }: { ssoRedirectPath?: string; userEmail?: string } = {},
      ): void => {
        if (!providerId || !assertOnline(t)) {
          return;
        }

        setIsLoadingSSOSignIn(true);
        void signInWithSSO({
          providerId,
          callbackPath: ssoRedirectPath ?? window.location.pathname,
          userEmail,
        })
          .catch(() => {
            toast.error(t((r) => r["auth.sign-in.error.sso-unavailable"]));
          })
          .finally(() => {
            setIsLoadingSSOSignIn(false);
          });
      },
      [t, validatedEmail],
    );

    const autoSSOSignIn = useCallback(
      ({
        providerId,
        ssoRedirectPath,
        userEmail,
      }: {
        providerId: string;
        ssoRedirectPath?: string;
        userEmail: string;
      }) => {
        if (hasActiveSSOAttempt(providerId)) {
          log("Automatic SSO skipped: a recent attempt is in progress", "warn", {
            email: userEmail,
            sso_provider_id: providerId,
          });
          return;
        }
        ssoSignIn(providerId, { ssoRedirectPath, userEmail });
      },
      [ssoSignIn],
    );

    const updateValidatedEmail = useCallback(
      async (
        email: string,
        { ssoRedirectPath, autoSignIn = true }: { ssoRedirectPath?: string; autoSignIn?: boolean } = {},
        // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: not that complex, mostly error handling
      ): Promise<void> => {
        if (isPreview()) {
          setValidatedEmail(email);
          setIsLoadingSSOProviderId(false);
          setSSOProviderId(false);
          return;
        }
        if (!assertOnline(t)) {
          setValidatedEmail(email);
          setIsLoadingSSOProviderId(false);
          return;
        }
        setIsLoadingSSOProviderId(true);
        try {
          const domain = email.split("@")[1];
          // TODO: can we move to trpc?
          // Currently all the config is in the front app so can't be used here
          const response = await fetchWithRetry(`${getBaseUrl()}/api/auth/get-tenant-config?domain=${domain}`, {
            credentials: "include",
          });
          if (!response.ok) {
            toast.error(t((r) => r["auth.sign-in.error.sso-unavailable"]));
            alertDevs({
              error: new Error("SSO tenant config request returned an error status"),
              context: { status: response.status, body: await response.text() },
            });
            return;
          }
          const parsedResponse = TenantConfigResponseSchema.safeParse(await response.json());
          if (!parsedResponse.success) {
            toast.error(t((r) => r["auth.sign-in.error.sso-unavailable"]));
            alertDevs({
              error: new Error("Failed to parse SSO provider ID response"),
              context: { parsedResponse, error: parsedResponse.error },
            });
            return;
          }

          const providerId = parsedResponse.data.providerId;
          if (providerId) {
            setSSOProviderId(providerId);

            if (autoSignIn && !isPreview()) {
              autoSSOSignIn({ providerId, ssoRedirectPath, userEmail: email });
            }
          } else {
            setSSOProviderId(false);
          }
        } catch (error) {
          // Deliberately not awaited: the form must stop loading before the reachability probe resolves.
          void reportTenantConfigFailure(error, t);
        } finally {
          setValidatedEmail(email);
          setIsLoadingSSOProviderId(false);
        }
      },
      [setSSOProviderId, setValidatedEmail, t, autoSSOSignIn],
    );

    useHandleOnboarding({ validatedEmail, updateValidatedEmail });

    const showSSOButton = React.useMemo(() => {
      return !ssoError && !!ssoProviderId && !isPreview();
    }, [ssoProviderId, ssoError]);

    // Re-fetch SSO provider when email is known but provider state was cleared (e.g. after logout)
    const ssoInitRun = React.useRef(false);
    React.useEffect(() => {
      if (ssoInitRun.current) {
        return;
      }
      ssoInitRun.current = true;
      // Skip while sign-out is in flight: authSignOut is about to hard-reload to /sign-in,
      if (validatedEmail && ssoProviderId === null && !isSigningOut()) {
        void updateValidatedEmail(validatedEmail, { autoSignIn: false });
      }
    }, [ssoProviderId, updateValidatedEmail, validatedEmail]);

    React.useEffect(() => {
      if (!isSuccessPage) {
        return;
      }
      const timerId = setTimeout(() => {
        window.location.replace("/sign-in");
      }, 5000);
      return () => {
        clearTimeout(timerId);
      };
    }, [isSuccessPage]);

    if (isSuccessPage) {
      return (
        <div className="mx-auto mt-20 w-lg">
          <Card>
            <CardHeader>
              <CardTitle>
                <Trans i18nKey="auth.sign-in.success.title" />
              </CardTitle>
              <CardDescription>
                <Trans i18nKey="auth.sign-in.success.message" />
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      );
    }

    return (
      <div className="mx-auto mt-20 w-lg">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">
              <Trans i18nKey="auth.sign-in.title" />
            </CardTitle>
            <CardDescription>
              <Trans i18nKey="auth.sign-in.subtitle" />
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <SignInForm
              validatedEmail={validatedEmail}
              updateValidatedEmail={updateValidatedEmail}
              resetValidatedEmail={resetValidatedEmail}
              hasExpiredError={hasExpiredError}
              hasInvalidError={hasInvalidError}
              loading={isLoadingSSOProviderId}
            />

            {showSSOButton ? (
              <Button
                variant={{ kind: "primary", renderMode: "fill", size: "m" }}
                className={cn("w-full", { hidden: !validatedEmail })}
                type="button"
                isPending={isLoadingSSOSignIn}
                enableCheckmark
                // oxlint-disable-next-line jsx-no-new-function-as-prop -- LINTER migration follow up
                onClick={() => ssoSignIn(ssoProviderId)}
              >
                <Trans i18nKey="auth.sign-in.button.sso" />
              </Button>
            ) : (
              <div className={cn({ hidden: !validatedEmail })}>
                <OtherMethods validatedEmail={validatedEmail} />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  },
);
SignInPage.displayName = "SignInPage";
